import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import clsx from "clsx";
import { useToggle } from "usehooks-ts";

import truckEmpty from "assets/images/truck-empty.png";
import Button from "components/Button";
import ConfirmationModal from "components/ConfirmationModal";
import Input from "components/Input";
import Modal from "components/Modal";
import Select from "components/Select";
import StatusModal from "components/StatusModal";
import { ExclamationSVGIcon } from "components/SVGIcons";
import { COMPANY_TYPE } from "constants/haulpay";
import { routes } from "constants/routes";
import CreditItem from "features/main/HaulPay/modules/Shared/CreditItem";
import CreditItemSkeleton from "features/main/HaulPay/modules/Shared/CreditItem/CreditItemSkeleton";
import { formatNumber } from "helpers/formatNumber";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import { useUserFeature } from "hooks/useUserFeature";
import { ICredit, ICreditItem } from "models/HaulPay";
import {
  addCustomerAPI,
  myCustomersAPI,
  requestIncreaseAPI,
  sendNoaAPI
} from "services/api/haulpay";
import { selectUserDetails } from "store/user/selector";
import styles from "./style.module.scss";
import { mdiLoading } from "@mdi/js";
import Icon from "@mdi/react";
import DollarInput from "components/DollarInput";
import { useAnalytics } from "hooks/useAnalytics";
import useInfiniteScroll from "../../../../../../hooks/useInfiniteScroll";
import useAppStateChange from "../../../../../../hooks/useAppStateChange";
import { usePullToRefresh } from "context/PullToRefreshContext";
import { RefLoadingSpinner } from "../../../../../../components/LoadingSpinner";
import dollarsToCents from "helpers/dollarsToCents";

export default function MyCustomers() {
  const navigate = useNavigate();
  const { isBroker } = useUserFeature();
  const { isDesktop } = useDeviceType();
  const userDetails = useSelector(selectUserDetails);
  const { trackAllEvents } = useAnalytics();
  const ContainerRef = useRef<HTMLDivElement>(null);
  const [isWarningModalOpen, toggleWarningModal] = useToggle();
  const [isAddWarningModalOpen, toggleAddWarningModal] = useToggle();
  const [isCreditWarningModalOpen, toggleCreditWarningModal] = useToggle();
  const [isNoaStatusModalOpen, toggleNoaStatusModal] = useToggle();

  const [isSendingRequest, setIsSendingRequest] = useState(false);
  const [searchFilter, setSearchFilter] = useState<any>();
  const [selectedData, setSelectedData] = useState<{
    id: string;
    relationship_id: string;
    dba: string;
    credit_available: number;
  }>({
    id: "",
    relationship_id: "",
    dba: "",
    credit_available: 0
  });

  const {
    register,
    control,
    formState: { isSubmitting },
    handleSubmit
  } = useForm<Partial<ICredit>>({
    values: {
      companyType: COMPANY_TYPE[0]
    }
  });

  const methods = useForm<{
    creditAmount: number;
  }>();

  const {
    watch: creditWatch,
    formState: { isSubmitting: isCreditSubmitting },
    handleSubmit: handleCreditSubmit
  } = methods;

  const {
    data: customers,
    loading: isLoadingMore,
    observerRef,
    resetPagination,
    paginationInfo,
    fetch: fetchCustomers
  } = useInfiniteScroll<ICreditItem>(
    (params: any) => fetchFactoringDebtors(params),
    {
      threshold: 0.9,
      concatData: true
    },
    [userDetails?.factoring_id]
  );

  const refreshCustomers = () => fetchCustomers({ offset: 0, add: false });
  useAppStateChange((isActive) => isActive && refreshCustomers());
  usePullToRefresh(refreshCustomers);

  const fetchFactoringDebtors = useCallback(
    async (params: any) => {
      try {
        const filter = {
          ...params,
          ...searchFilter
        };

        return await myCustomersAPI(filter);
      } catch {
        showErrorMessage("Please try again later.");
      }
    },
    [searchFilter]
  );

  const sendNoa = async (debtorId: string, clientId: string) => {
    const formData = {
      auto_send_noa: true,
      debtor: debtorId,
      client_id: clientId
    };

    try {
      await sendNoaAPI(formData);
      showSuccessMessage("NOA has been sent.");
      resetPagination();
      toggleNoaStatusModal();
    } catch (error: any) {
      showErrorMessage(error.message);
    }
  };

  const requestIncrease = async ({ creditAmount }: { creditAmount: number }) => {
    const formData = {
      id: selectedData.relationship_id,
      amount: dollarsToCents(creditAmount)
    };

    try {
      setIsSendingRequest(true);
      await requestIncreaseAPI(formData);
      toggleWarningModal();
      toggleAddWarningModal();
      trackAllEvents("haulpay_credit_customer_request_increase");
    } catch (err: any) {
      showErrorMessage(err.message);
    } finally {
      setIsSendingRequest(false);
    }
  };

  const reviewCredit = async (credit_approved: string, id: string) => {
    if (credit_approved === "pending") {
      const formData = {
        id: id,
        credit_approved: "needs_review"
      };
      try {
        await addCustomerAPI(formData);
        resetPagination();
        toggleCreditWarningModal();
      } catch (error: any) {
        showErrorMessage(error.message);
      }
    }
  };

  const goToHistory = (data: any) => {
    navigate(isBroker ? routes.HAULPAY_BROKER_MANAGE_PAY : routes.HAULPAY_CARRIER_MANAGE_PAY, {
      state: {
        debtor: data?.id,
        debtorName: data?.company_name
      }
    });
  };

  const onSubmit = (data: any) => {
    const formData = {
      company_name: data.companyName,
      [data.companyType.value]: data.number
    };

    setSearchFilter(formData);
  };

  useEffect(() => {
    searchFilter && resetPagination();
  }, [searchFilter]);

  return (
    <div className={styles.myCustomersContainer} ref={ContainerRef}>
      <div className={styles.customerNumber}>
        <div className="flex gap-1.5 text-xl">
          <span>Your Customers :</span>
          <span className="font-medium">{paginationInfo.totalCount}</span>
        </div>
      </div>

      <form className={styles.customerWrapper} onSubmit={handleSubmit(onSubmit)}>
        <Input
          name="companyName"
          register={register}
          label="&nbsp;&nbsp;Company Name"
          className="desktop:w-1/4"
        />

        <div className={styles.companyName}>
          {isDesktop ? <span>Or</span> : null}
          <Controller
            name="companyType"
            control={control}
            render={({ field: { value = COMPANY_TYPE[0], onChange } }) => (
              <Select
                options={COMPANY_TYPE}
                value={value}
                onChange={onChange}
                {...(!isDesktop && { label: "Or" })}
                {...(!isDesktop && { labelClass: "pl-1.5" })}
                className="w-28 desktop:w-40"
              />
            )}
          />
          <Input
            name="number"
            register={register}
            className="flex-1"
            {...(!isDesktop && { label: "debtor" })}
            {...(!isDesktop && { labelClass: "opacity-0" })}
          />
        </div>

        <div className={styles.customerFooter}>
          <Button type="submit" loading={isSubmitting}>
            Search Credit
          </Button>
        </div>
      </form>

      {isLoadingMore && customers.length === 0 ? (
        <>
          {[...Array(3)].map((_, index) => (
            <CreditItemSkeleton key={index} />
          ))}
        </>
      ) : customers.length ? (
        <>
          {customers.map((item) => (
            <CreditItem
              key={item.id}
              data={item}
              addCustomer={reviewCredit}
              setSelectedData={setSelectedData}
              goToHistory={goToHistory}
              toggleWarningModal={toggleWarningModal}
              toggleNoaStatusModal={toggleNoaStatusModal}
              isEdit
            />
          ))}
          {paginationInfo.hasNextPage && (
            <RefLoadingSpinner observerRef={observerRef} isLoadingMore={isLoadingMore} />
          )}
        </>
      ) : (
        <div className={styles.myCustomerEmpty}>
          <img src={truckEmpty} alt="Empty truck list" />
          <h3 className={styles.myCustomerEmptyTitle}>No results found.</h3>
          <p className="font-medium text-center">Try refining your search to view results.</p>
        </div>
      )}

      <ConfirmationModal
        title="Warning"
        content="We are checking their credit now and will alert you of approval within 1 business day. Thank you."
        isConfirmModalOpen={isAddWarningModalOpen}
        toggleConfirmModal={toggleAddWarningModal}
        isHideCancelButton
      />

      <Modal
        isOpen={isWarningModalOpen}
        closeModal={toggleWarningModal}
        wrapperClass="!my-auto desktop:!min-h-fit desktop:!w-1/2 text-sm"
        dialogClass="!bg-foreground !px-3 !py-4 gap-2.5 desktop:!px-5 desktop:!py-7"
        closeClass="!top-24 !right-6 !bg-transparent !text-text">
        <div className="flex items-center gap-1 text-xl font-bold text-primary">
          <ExclamationSVGIcon />
          <span className="desktop:text-2xl">Warning</span>
        </div>
        <FormProvider {...methods}>
          <form onSubmit={handleCreditSubmit(requestIncrease)}>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-2 desktop:text-base">
                <div className="flex gap-1.5">
                  <span>Customer Name:</span>
                  <span className="font-bold">{selectedData.dba}</span>
                </div>

                <div className="flex gap-1.5">
                  <span>Current Available Credit:</span>
                  <span className="font-bold">
                    {formatNumber(selectedData.credit_available / 100, 2)}
                  </span>
                </div>
              </div>

              <p className="mt-4">
                Please enter the amount of additional credit you would like this increased by:
              </p>

              <DollarInput
                name="creditAmount"
                label="Request Credit Increase Amount"
                placeholder="$0.00"
                inputClass="!border-subtitle !rounded-lg text-sm desktop:text-base"
                labelClass="text-text"
                rules={{
                  required: "Amount is required",
                  min: { value: 0.01, message: "Amount must be greater than $0" }
                }}
              />
            </div>

            <div className="flex justify-between gap-3 mt-6 text-xs desktop:text-base">
              <Button
                type="button"
                variant="outline"
                rounded
                className="!px-12 !h-9"
                onClick={toggleWarningModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                rounded
                className={clsx(
                  "!px-12 !h-9",
                  creditWatch("creditAmount")
                    ? "bg-gradient-to-r from-primary to-secondary"
                    : "!bg-subtitle !ring-subtitle cursor-not-allowed"
                )}
                disabled={isSendingRequest || !creditWatch("creditAmount") || isCreditSubmitting}>
                {isSendingRequest || isCreditSubmitting ? (
                  <Icon path={mdiLoading} size={1.5} spin={1} />
                ) : (
                  "Request"
                )}
              </Button>
            </div>
          </form>
        </FormProvider>
      </Modal>

      <ConfirmationModal
        title={`Are you sure you want to submit a Notice of Assignment (NOA) in advance for ${selectedData.dba}?`}
        content="Only proceed if you're already set up with this debtor and their Accounts Payable team.
        Submitting early without a working relationship can hurt your credibility and delays processing for everyone."
        isConfirmModalOpen={isNoaStatusModalOpen}
        toggleConfirmModal={toggleNoaStatusModal}
        labelCancelButton="Yes"
        labelOKButton="No"
        onCancel={() => sendNoa(selectedData.id, userDetails?.factoring_id)}
        onOK={toggleNoaStatusModal}
      />

      <StatusModal
        title="Warning"
        content="We are checking their credit now and will alert you of approval within 1 business day. Thank you."
        isModalOpen={isCreditWarningModalOpen}
        toggleModal={toggleCreditWarningModal}
        backdropClick={toggleCreditWarningModal}
      />
    </div>
  );
}
